import Swiper from 'swiper'
import {
  EffectFade,
  Navigation,
  Autoplay,
  Grid,
  Pagination,
} from 'swiper/modules'
import { SwiperOptions } from 'swiper/types'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/autoplay'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/grid'

type TConfigNames =
  | 'articles'
  | 'products'
  | 'fade'
  | 'grid'
  | 'recommendations'
  | 'offers'
  | 'recommendations2'
  | 'team'

interface IConfigProps {
  swiperContainer: Element
  configName: TConfigNames
}

const getConfigs = (): { [key in TConfigNames]: SwiperOptions } => {
  return {
    articles: {
      modules: [Navigation],
      watchSlidesProgress: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 4,
      speed: 300,
      breakpoints: {
        1366: {
          slidesPerView: 4,
          slidesPerGroup: 1,
          spaceBetween: 4,
        },
        992: {
          slidesPerView: 3,
          slidesPerGroup: 1,
          spaceBetween: 4,
        },
        768: {
          slidesPerView: 2,
          slidesPerGroup: 1,
          spaceBetween: 4,
        },
        468: {
          slidesPerView: 2,
          slidesPerGroup: 1,
          spaceBetween: 4,
        },
      },
    },
    products: {
      modules: [Navigation],
      slidesPerView: 'auto',
      watchSlidesProgress: true,
      slidesPerGroup: 1,
      spaceBetween: 10,
      speed: 300,
      breakpoints: {
        576: {
          slidesPerView: 3,
          slidesPerGroup: 1,
          spaceBetween: 15,
        },
        468: {
          slidesPerView: 2,
          slidesPerGroup: 1,
          spaceBetween: 10,
        },
      },
    },
    fade: {
      modules: [Navigation, EffectFade, Autoplay, Pagination],
      slidesPerView: 1,
      slidesPerGroup: 1,
      speed: 300,
      effect: 'fade',
      autoplay: {
        delay: 6000,
        disableOnInteraction: false,
      },
    },
    grid: {
      modules: [Grid, Navigation],
      slidesPerView: 2,
      slidesPerGroup: 1,
      spaceBetween: 2,
      breakpoints: {
        1200: {
          slidesPerView: 4,
          slidesPerGroup: 1,
          spaceBetween: 2,
          grid: {
            rows: 2,
            fill: 'row',
          },
        },
        992: {
          slidesPerView: 4,
          slidesPerGroup: 1,
          grid: undefined,
        },
        768: {
          slidesPerView: 3,
          slidesPerGroup: 1,
        },
      },
    },
    recommendations: {
      modules: [Navigation, EffectFade, Autoplay, Pagination],
      slidesPerView: 1,
      slidesPerGroup: 1,
      speed: 300,
      effect: 'fade',
      pagination: {
        el: '.swiper-pagination',
        type: 'fraction',
      },
    },
    offers: {
      modules: [Pagination],
      slidesPerView: 1,
      slidesPerGroup: 1,
      watchSlidesProgress: true,
      spaceBetween: 10,
      speed: 300,
      pagination: {
        el: '.swiper-pagination',
      },
      breakpoints: {
        768: {
          enabled: false,
        },
        576: {
          slidesPerView: 2,
        },
      },
    },
    team: {
      modules: [Navigation],
      slidesPerView: 1,
      slidesPerGroup: 1,
      watchSlidesProgress: true,
      spaceBetween: 10,
      speed: 300,
      breakpoints: {
        992: {
          enabled: false,
        },
        768: {
          slidesPerView: 3,
        },
        576: {
          slidesPerView: 2,
        },
      },
    },
    recommendations2: {
      modules: [Navigation],
      watchSlidesProgress: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 20,
      speed: 300,
      breakpoints: {
        992: {
          slidesPerView: 2,
          slidesPerGroup: 1,
          spaceBetween: 30,
        },
      },
    },
  }
}

const setConfig = ({ swiperContainer, configName }: IConfigProps) => {
  const navPrev = swiperContainer.querySelector('[data-controls="prev"]')
  const navNext = swiperContainer.querySelector('[data-controls="next"]')
  const pagination = swiperContainer.parentElement?.querySelector(
    '[data-controls="pagination"]',
  )

  let newConfigs: ReturnType<typeof getConfigs> = getConfigs()

  if (navPrev && navNext) {
    newConfigs = {
      ...newConfigs,
      [configName]: {
        ...newConfigs[configName],
        navigation: {
          prevEl: navPrev,
          nextEl: navNext,
        },
      },
    }
  }

  if (pagination) {
    newConfigs = {
      ...newConfigs,
      [configName]: {
        ...newConfigs[configName],
        pagination: { el: pagination, clickable: true },
      },
    }
  }

  return newConfigs[configName]
}

const getSwiper = ({ swiperContainer, configName }: IConfigProps) => {
  const swiper = new Swiper(
    swiperContainer as HTMLElement,
    setConfig({ swiperContainer, configName }),
  )

  return swiper
}

window.addEventListener('load', () => {
  let arrElements: { el: Element; name: TConfigNames }[] = []

  Object.keys(getConfigs()).forEach((name) => {
    const elements = document.querySelectorAll(`[data-slider="${name}"]`)

    if (elements.length === 0) return

    elements.forEach((el) => {
      arrElements = [...arrElements, { el, name: name as TConfigNames }]
    })
  })

  arrElements.forEach((element) => {
    getSwiper({ swiperContainer: element.el, configName: element.name })
  })
})
